import { gql } from "@apollo/client";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { useLayoutEffect, useMemo, useState } from "react";
import type {
  AutocompleteTasksQuery,
  AutocompleteTasksQueryVariables,
} from "@/graphql/types";

export interface AutocompleteTaskResult {
  id: string;
  name: string;
  description?: string | null;
  isWarehouse?: boolean;
}

const TASKS_BY_IDS = gql`
  query TasksByIds($filters: TaskFilters!, $pagination: PaginationOptions!) {
    searchTasks(filters: $filters, pagination: $pagination) {
      results {
        id
        name
      }
    }
  }
`;

export const useTasksByIds = (ids: string[]) => {
  const { data, loading } = useQuery<{
    searchTasks: { results: { id: string; name?: string | null }[] };
  }>(TASKS_BY_IDS, {
    variables: {
      filters: { taskIds: ids },
      pagination: { perPage: ids.length },
    },
    skip: ids.length === 0,
    fetchPolicy: "no-cache",
  });

  return {
    tasks: (data?.searchTasks.results ?? []).map((r) => ({
      id: r.id,
      name: r.name ?? r.id,
    })),
    loading,
  };
};

type AutocompleteTasksVariables = AutocompleteTasksQueryVariables & {
  skip?: boolean;
};

export const AUTOCOMPLETE_TASKS = gql`
  query AutocompleteTasks(
    $query: String
    $limit: Int
    $projectIds: [ID]
    $filter: AutocompleteTaskFilter
    $cursor: String
  ) {
    autocompleteTasks(
      query: $query
      limit: $limit
      projectIds: $projectIds
      filter: $filter
      cursor: $cursor
    ) {
      results {
        id
        name
        description
        isWarehouse
      }
      hasMore
      cursor
    }
  }
`;

export const useAutocompleteTasksQuery = (
  variables: AutocompleteTasksVariables = {},
  fetchPolicy: "cache-and-network" | "network-only" = "cache-and-network",
) => {
  const { skip, ...queryVariables } = variables;
  const client = useApolloClient();
  const requestKey = JSON.stringify(queryVariables);
  const owner = useMemo(
    () => ({ requestKey, active: !skip, pending: false }),
    [requestKey, skip],
  );
  const [pageState, setPageState] = useState<{
    owner: typeof owner;
    error?: Error;
  }>();
  useLayoutEffect(() => {
    owner.active = !skip;
    return () => {
      owner.active = false;
    };
  }, [owner, skip]);

  const { data, loading, error, refetch } = useQuery<
    AutocompleteTasksQuery,
    AutocompleteTasksQueryVariables
  >(AUTOCOMPLETE_TASKS, {
    variables: {
      limit: 250,
      ...queryVariables,
    },
    skip,
    fetchPolicy,
  });

  const loadMore = async () => {
    const page = data?.autocompleteTasks;
    if (
      skip ||
      loading ||
      !owner.active ||
      owner.pending ||
      !page?.hasMore ||
      !page.cursor
    )
      return;
    owner.pending = true;
    setPageState({ owner });
    let pageError: Error | undefined;
    try {
      // Independent pages cannot hold up a new search on the base observable.
      const { data: next } = await client.query<
        AutocompleteTasksQuery,
        AutocompleteTasksQueryVariables
      >({
        query: AUTOCOMPLETE_TASKS,
        variables: { limit: 250, ...queryVariables, cursor: page.cursor },
        fetchPolicy: "no-cache",
      });
      if (!owner.active || !next) return;
      client.cache.updateQuery<
        AutocompleteTasksQuery,
        AutocompleteTasksQueryVariables
      >(
        {
          query: AUTOCOMPLETE_TASKS,
          variables: { limit: 250, ...queryVariables },
        },
        (previous) =>
          previous
            ? {
                ...next,
                autocompleteTasks: {
                  ...next.autocompleteTasks,
                  results: [
                    ...previous.autocompleteTasks.results,
                    ...next.autocompleteTasks.results.filter(
                      (row) =>
                        !previous.autocompleteTasks.results.some(
                          (existing) => existing.id === row.id,
                        ),
                    ),
                  ],
                },
              }
            : previous,
      );
    } catch (failure) {
      pageError =
        failure instanceof Error
          ? failure
          : new Error("Task autocomplete page failed");
    } finally {
      owner.pending = false;
      if (owner.active) setPageState({ owner, error: pageError });
    }
  };

  return {
    tasks: data?.autocompleteTasks.results || [],
    hasMore: data?.autocompleteTasks.hasMore || false,
    loading: loading || (pageState?.owner === owner && owner.pending),
    error: error ?? (pageState?.owner === owner ? pageState.error : undefined),
    refetch: () => {
      if (pageState?.owner === owner && pageState.error && !error)
        return loadMore();
      return refetch();
    },
    loadMore,
  };
};
